import { readToken } from "@/lib/auth-token";

import type { Json } from "./types";

/**
 * 极薄的一层 fetch 封装。
 *
 * 约定：
 * - 基址来自 `VITE_API_BASE_URL`，默认 `/api/v1`（开发期由 Vite 代理到 FastAPI）；
 * - 后端统一错误信封 `{ error: { code, message, details, request_id } }`，在此解包成 `ApiError`；
 * - 任何网络/协议错误都**直接抛出**，由视图呈现错误态。
 *   这里没有、也不允许有「失败就回落 mock」的分支（CONSTITUTION D1 / RULES §11.2）。
 */

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

/** 强制走本地 mock（无后端演示、截图回归、单元测试）。 */
export const USE_MOCK: boolean = import.meta.env.VITE_USE_MOCK === "1";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, Json>;
  readonly requestId: string | null;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, Json>;
    requestId?: string | null;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details ?? {};
    this.requestId = args.requestId ?? null;
  }
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, Json>;
    request_id?: string;
  };
}

function looksLikeEnvelope(value: unknown): value is ErrorEnvelope {
  return typeof value === "object" && value !== null && "error" in value;
}

async function parseError(response: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (looksLikeEnvelope(payload) && payload.error) {
    return new ApiError({
      code: payload.error.code ?? "UNKNOWN",
      message: payload.error.message ?? response.statusText,
      status: response.status,
      details: payload.error.details ?? {},
      requestId: payload.error.request_id ?? null,
    });
  }
  return new ApiError({
    code: "HTTP_ERROR",
    message: response.statusText || `HTTP ${response.status}`,
    status: response.status,
  });
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** 幂等键，透传为 `Idempotency-Key`（V106）。 */
  idempotencyKey?: string;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    url.searchParams.set(key, String(value));
  }
  return `${url.pathname}${url.search}`;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, idempotencyKey, headers, ...rest } = options;
  // `FormData`（文件上传，如封面 `POST /projects/{id}/cover`）**不设** Content-Type、
  // 也**不** JSON 序列化：边界字符串必须由浏览器自己带（含 multipart boundary）。
  const isForm = body instanceof FormData;
  const finalHeaders = new Headers(headers);
  if (body !== undefined && !isForm && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }
  if (idempotencyKey) finalHeaders.set("Idempotency-Key", idempotencyKey);
  // 登录态注入（specs/15 §2）：有 token 就带 Bearer；没有则交给后端的开发态回退。
  if (!finalHeaders.has("Authorization")) {
    const token = readToken();
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
